import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { CanopyCurveProto, CanopyNode } from './canopy-curve-proto';

@Component({
	selector: 'canopy-curve-proto',
	templateUrl: './canopy-curve-proto.component.html',
	styleUrls: ['./canopy-curve-proto.component.scss']
})
export class CanopyCurveProtoComponent {
	@ViewChild('chartSvg', { static: true }) public chartSvg: ElementRef;

	public crop = 'lettuce';
	public wetDate = new Date(2026, 3, 1);
	public harvestDate = new Date(2026, 5, 15);
	public maxDay: number;
	public cuts: number[] = [];
	public nodes: CanopyNode[] = [];
	public ndvi: CanopyNode[] = [];
	public ndviFit: CanopyNode[] = [];
	public savedNodes: CanopyNode[] = null;
	public selectedIndex: number = null;
	public queryDate: string;
	public queryResult: number = null;
	public queryUsedCustom = false;
	public wetInput: string;
	public harvestInput: string;
	public showNdviFit = false;
	public vbX = 0;
	public vbY = 0;
	public vbW = 720;
	public vbH = 340;

	public readonly W = 720;
	public readonly H = 340;
	public readonly pad = { l: 48, r: 16, t: 16, b: 36 };

	private _dragIndex: number = null;
	private _didDrag = false;
	private _panning = false;
	private _panLast: { x: number, y: number } = null;

	constructor() {
		this.reload();
	}

	public onCropChange(): void {
		if (this.crop === 'alfalfa') {
			this.harvestDate = new Date(2026, 8, 15);
		} else {
			this.harvestDate = new Date(2026, 5, 15);
		}

		this.reload();
	}

	public get plotW(): number {
		return this.W - this.pad.l - this.pad.r;
	}

	public get plotH(): number {
		return this.H - this.pad.t - this.pad.b;
	}

	public get seedSegments(): string[] {
		return this._toPolylines(CanopyCurveProto.dailySeed(this.maxDay, this.crop, this.cuts));
	}

	public get ndviFitSegments(): string[] {
		return this._toPolylines(this.ndviFit);
	}

	public get calculatedSegments(): string[] {
		return this._toPolylines(CanopyCurveProto.dailySpline(this.nodes, this.maxDay, this.cuts));
	}

	public get yTicks(): number[] {
		return [0, 25, 50, 75, 100];
	}

	public x(day: number): number {
		return this.pad.l + (day / this.maxDay) * this.plotW;
	}

	public y(canopy: number): number {
		return this.pad.t + (1 - canopy / 100) * this.plotH;
	}

	public applyNdviFit(): void {
		this.nodes = CanopyCurveProto.nodesFromDaily(this.ndviFit, this.cuts, this.maxDay);
		this.selectedIndex = null;
	}

	public save(): void {
		this.savedNodes = CanopyCurveProto.copyNodes(this.nodes);
	}

	public reset(): void {
		this.nodes = CanopyCurveProto.sampleSeedNodes(this.maxDay, this.crop, this.cuts);
		this.savedNodes = null;
		this.selectedIndex = null;
		this.queryResult = null;
	}

	public canRemoveSelected(): boolean {
		if (this.selectedIndex === null) {
			return false;
		}

		return !CanopyCurveProto.isCycleBoundary(
			this.nodes[this.selectedIndex].day, this.cuts, this.maxDay);
	}

	public removeSelected(): void {
		if (!this.canRemoveSelected()) {
			return;
		}

		this.nodes.splice(this.selectedIndex, 1);
		this.selectedIndex = null;
	}

	public calculate(): void {
		let date = CanopyCurveProto.fromInputDate(this.queryDate);
		let day: number;

		if (!date) {
			this.queryResult = null;
			return;
		}

		day = CanopyCurveProto.clamp(
			CanopyCurveProto.daysBetween(this.wetDate, date), 0, this.maxDay);

		this.queryUsedCustom = !!this.savedNodes;
		this.queryResult = this.queryUsedCustom
			? CanopyCurveProto.splineCanopy(this.savedNodes, day, this.cuts, this.maxDay)
			: CanopyCurveProto.seedCanopy(day, this.maxDay, this.crop, this.cuts);
	}

	public startDrag(index: number, event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this._dragIndex = index;
		this.selectedIndex = index;
		this._didDrag = false;
	}

	public onPlotClick(event: MouseEvent): void {
		let loc: { x: number, y: number };
		let day: number;
		let canopy: number;
		let i: number;

		if (this._didDrag) {
			return;
		}

		loc = this._svgPoint(event);
		day = Math.round(this._xToDay(loc.x));
		canopy = CanopyCurveProto.clamp(this._yToCanopy(loc.y), 0, 100);

		if (day <= 0 || day >= this.maxDay || this.cuts.indexOf(day) >= 0) {
			return;
		}

		if (this.nodes.some(n => n.day === day)) {
			return;
		}

		for (i = 1; i < this.nodes.length; i++) {
			if (this.nodes[i].day > day) {
				this.nodes.splice(i, 0, { day: day, canopy: canopy });
				this.selectedIndex = i;
				return;
			}
		}
	}

	@HostListener('document:mousemove', ['$event'])
	public onMouseMove(event: MouseEvent): void {
		let loc: { x: number, y: number };
		let node: CanopyNode;
		let minDay: number;
		let maxDay: number;

		if (this._panning && this._panLast) {
			let svg = this.chartSvg.nativeElement as SVGSVGElement;
			let rect = svg.getBoundingClientRect();

			this.vbX -= (event.clientX - this._panLast.x) / rect.width * this.vbW;
			this.vbY -= (event.clientY - this._panLast.y) / rect.height * this.vbH;
			this._panLast = { x: event.clientX, y: event.clientY };
			this._clampView();
			this._didDrag = true;
			return;
		}

		if (this._dragIndex === null) {
			return;
		}

		this._didDrag = true;
		loc = this._svgPoint(event);
		node = this.nodes[this._dragIndex];
		node.canopy = CanopyCurveProto.clamp(this._yToCanopy(loc.y), 0, 100);

		if (!CanopyCurveProto.isCycleBoundary(node.day, this.cuts, this.maxDay)) {
			minDay = this.nodes[this._dragIndex - 1].day + 1;
			maxDay = this.nodes[this._dragIndex + 1].day - 1;
			node.day = CanopyCurveProto.clamp(Math.round(this._xToDay(loc.x)), minDay, maxDay);
		}
	}

	@HostListener('document:mouseup')
	public onMouseUp(): void {
		this._dragIndex = null;
		this._panning = false;
		this._panLast = null;
		setTimeout(() => { this._didDrag = false; }, 0);
	}

	public startPan(event: MouseEvent): void {
		if (this._dragIndex !== null || this.vbW >= this.W - 0.5) {
			return;
		}

		this._panning = true;
		this._panLast = { x: event.clientX, y: event.clientY };
		this._didDrag = false;
	}

	public onWheel(event: WheelEvent): void {
		let loc = this._svgPoint(event);
		let factor = event.deltaY < 0 ? 0.8 : 1.25;
		let newW = CanopyCurveProto.clamp(this.vbW * factor, this.W / 10, this.W);
		let newH = CanopyCurveProto.clamp(this.vbH * factor, this.H / 10, this.H);

		event.preventDefault();
		this.vbX = loc.x - (loc.x - this.vbX) * (newW / this.vbW);
		this.vbY = loc.y - (loc.y - this.vbY) * (newH / this.vbH);
		this.vbW = newW;
		this.vbH = newH;
		this._clampView();
	}

	public zoomBy(factor: number): void {
		let cx = this.vbX + this.vbW / 2;
		let cy = this.vbY + this.vbH / 2;
		let newW = CanopyCurveProto.clamp(this.vbW * factor, this.W / 10, this.W);
		let newH = CanopyCurveProto.clamp(this.vbH * factor, this.H / 10, this.H);

		this.vbX = cx - newW / 2;
		this.vbY = cy - newH / 2;
		this.vbW = newW;
		this.vbH = newH;
		this._clampView();
	}

	public resetView(): void {
		this.vbX = 0;
		this.vbY = 0;
		this.vbW = this.W;
		this.vbH = this.H;
	}

	private reload(): void {
		this.maxDay = CanopyCurveProto.daysBetween(this.wetDate, this.harvestDate);
		this.cuts = this.crop === 'alfalfa' ? [55, 110] : [];
		this.ndvi = CanopyCurveProto.fakeNdvi(this.maxDay, this.crop, this.cuts);
		this.ndviFit = CanopyCurveProto.ndviFitDaily(this.ndvi, this.maxDay, this.crop, this.cuts);
		this.nodes = CanopyCurveProto.sampleSeedNodes(this.maxDay, this.crop, this.cuts);
		this.savedNodes = null;
		this.selectedIndex = null;
		this.queryResult = null;
		this.wetInput = CanopyCurveProto.toInputDate(this.wetDate);
		this.harvestInput = CanopyCurveProto.toInputDate(this.harvestDate);
		this.queryDate = this.wetInput;
		this.resetView();
	}

	private _clampView(): void {
		this.vbW = CanopyCurveProto.clamp(this.vbW, this.W / 10, this.W);
		this.vbH = CanopyCurveProto.clamp(this.vbH, this.H / 10, this.H);
		this.vbX = CanopyCurveProto.clamp(this.vbX, 0, this.W - this.vbW);
		this.vbY = CanopyCurveProto.clamp(this.vbY, 0, this.H - this.vbH);
	}

	private _toPolylines(points: CanopyNode[]): string[] {
		return CanopyCurveProto.splitAtCuts(points, this.cuts)
			.map(seg => seg.map(n => this.x(n.day) + ',' + this.y(n.canopy)).join(' '));
	}

	private _xToDay(px: number): number {
		return ((px - this.pad.l) / this.plotW) * this.maxDay;
	}

	private _yToCanopy(py: number): number {
		return (1 - (py - this.pad.t) / this.plotH) * 100;
	}

	private _svgPoint(event: MouseEvent): { x: number, y: number } {
		let svg = this.chartSvg.nativeElement as SVGSVGElement;
		let pt = svg.createSVGPoint();
		let ctm = svg.getScreenCTM();

		pt.x = event.clientX;
		pt.y = event.clientY;

		if (!ctm) {
			return { x: 0, y: 0 };
		}

		pt = pt.matrixTransform(ctm.inverse());
		return { x: pt.x, y: pt.y };
	}
}
